import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription, filter } from "rxjs";

import {
    SECONDARY_COLOR_LIGHT,
    TRANSPARENT_COLOR,
} from "@hermes/aphrodite/expansion-panel";
import {
    AbstractProductVariant,
    Product,
    ProductSizeVariant,
    ProductWatchStrapVariant,
} from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import { isRetailOnlyStock } from "@hermes/fragments/product-utils";
import { PRODUCT_PAGE } from "@hermes/states/add-to-cart";
import { ApplePayFacade, ApplepayStatus } from "@hermes/states/apple-pay";
import { BasketFacade } from "@hermes/states/basket";
import { ProductPageFacade } from "@hermes/states/product-page";
import { skuToUrlSku } from "@hermes/utils-generic/helpers";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

@Component({
    selector: "h-watch-product-selector",
    templateUrl: "./watch-product-selector.component.html",
    styleUrls: ["./watch-product-selector.component.scss"],
})
export class WatchProductSelectorComponent
    implements OnInit, OnChanges, OnDestroy
{
    /** Current page product */
    @Input()
    public product: Product;

    /* Form for AddToCart */
    public variantsForm: FormGroup;

    public areAllWatchColorVariantsOOS: boolean;

    public areAllWatchLengthVariantsOOS: boolean;

    public isAddToCartButtonDisplayed = true;

    public subscription: Subscription = new Subscription();
    public toggleVariantCollapsed: {
        /** local watch size toggle state */
        watchSize: boolean;
        /** local strap color toggle state */
        strapColor: boolean;
        /** local strap length toggle state */
        strapLength: boolean;
    };

    public isMobile: boolean;

    public secondaryColorLight = SECONDARY_COLOR_LIGHT;
    public transparentColor = TRANSPARENT_COLOR;

    constructor(
        public readonly productPageFacade: ProductPageFacade,
        private changeDetectorRef: ChangeDetectorRef,
        private formBuilder: FormBuilder,
        private breakpointService: BreakpointService,
        private context: Context,
        private basketFacade: BasketFacade,
        private router: Router,
        private applePayFacade: ApplePayFacade,
    ) {}

    /**
     * Init selector form
     * Initialize toggle
     */
    public ngOnInit(): void {
        this.initForm();
        this.initToggleVariantCollapsed();
        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        this.isMobile = !value.matches;
                        this.changeDetectorRef.detectChanges();
                    }),
            );
        }

        this.subscription.add(
            this.applePayFacade.status$
                .pipe(
                    filter((status) => status === ApplepayStatus.CheckingForm),
                )
                .subscribe(() => this.applePayFacade.formChecked()),
        );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.product && changes.product.currentValue) {
            this.areAllWatchLengthVariantsOOS =
                this.areAllWatchLengthVariantsOutOfStock(this.product);

            this.areAllWatchColorVariantsOOS =
                this.areAllWatchColorVariantsOutOfStock(this.product) &&
                this.areAllWatchLengthVariantsOOS;

            this.initForm();
        }
    }

    /**
     * At loaded page, only size variants toggle is expanded
     */
    public initToggleVariantCollapsed(): void {
        this.toggleVariantCollapsed = {
            watchSize: false,
            strapColor: true,
            strapLength: true,
        };
    }

    public initForm(): void {
        this.isAddToCartButtonDisplayed = !isRetailOnlyStock(
            this.product.stock,
        );

        this.variantsForm = this.formBuilder.group({
            strapLengths: this.product.simpleAttributes.wristCircumferenceValue,
            watchSizes: this.product.simpleAttributes.modelSize,
            strapColors: {
                nbTours: this.product.simpleAttributes.nbTours,
                refColoris: this.product.simpleAttributes.refColoris,
            },
        });

        // strapColors form Control initialiser
        this.initStrapColorFormControl();
        // strap Lenghts form Control initialiser
        this.initStrapLengthsFormControl();
        // watch sizes form control initialiser
        this.initWatchSizesFormControl();
    }

    /**
     * When expanding a toggle, close all others.
     */
    public onExpand(
        event: { id: string; value: boolean },
        toggleKey: string,
    ): void {
        Object.keys(this.toggleVariantCollapsed)
            .filter((key) => key !== toggleKey)
            .forEach((key) => {
                this.toggleVariantCollapsed[key] = true;
            });

        this.toggleVariantCollapsed[toggleKey] =
            !this.toggleVariantCollapsed[toggleKey];

        this.changeDetectorRef.detectChanges();
        // dispatch action
        this.productPageFacade.openSelectorToggle(toggleKey);
        this.productPageFacade.showMoreVariant({
            id: toggleKey,
            isExpanded: !event.value,
        });
    }

    /**
     * Handle bottom border for starp lengths
     */
    public shouldRemoveBottomBorderForStraps(): boolean {
        return (
            this.isMobile &&
            this.toggleVariantCollapsed.strapLength &&
            !this.product.attributes.shippingInformation
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /** Performance track loop */
    public trackBySku(index: number, item: AbstractProductVariant): string {
        return `${index}-${item.sku}`;
    }

    /**
     * Add to Cart when call to action button is called
     */
    public addToCartAction(showNotifSuccess: boolean = true): void {
        this.basketFacade.addToCart({
            sku: this.product.sku,
            displayNotifOnSuccess: showNotifSuccess,
            itemPosition: 0,
            templateType: this.product.templateType,
            context: PRODUCT_PAGE,
        });
    }

    public areAllWatchColorVariantsOutOfStock(product: Product): boolean {
        return (
            product.variants !== undefined &&
            product.variants.strapColors &&
            product.variants.strapColors.every(
                (variant) =>
                    variant.stock &&
                    !variant.stock.ecom &&
                    !variant.stock.retail,
            )
        );
    }

    public areAllWatchLengthVariantsOutOfStock(product: Product): boolean {
        return (
            product.variants !== undefined &&
            product.variants.strapLengths &&
            product.variants.strapLengths.every(
                (variant) =>
                    variant.stock &&
                    !variant.stock.ecom &&
                    !variant.stock.retail,
            )
        );
    }

    /**
     * Build route array from variant attributes,
     * Manages edge cases with slash or spaces in sku and navigate into
     */
    private navigateToVariant(slug: string, sku: string): void {
        const skuParts = skuToUrlSku(sku).split("/");
        const routes: string[] = ["product", `${slug}-${skuParts.shift()}`];
        if (skuParts.length > 0) {
            routes.push(...skuParts);
        }
        // slash at the end with empty string
        this.router.navigate([...routes, ""], {
            state: { variant: true },
        });
    }

    /**
     * Initialize strapLengths form control
     */
    private initStrapLengthsFormControl(): void {
        const strapLengths = "strapLengths";

        this.subscription.add(
            this.variantsForm.controls[strapLengths].valueChanges.subscribe(
                (value) => {
                    const selectedVariant: AbstractProductVariant =
                        this.product.variants[strapLengths].find(
                            (variant: ProductSizeVariant) =>
                                variant.size === value,
                        );
                    this.navigateToVariant(
                        selectedVariant.slug,
                        selectedVariant.sku,
                    );
                },
            ),
        );
    }

    /**
     * Initialize watchSizes form control
     */
    private initWatchSizesFormControl(): void {
        const watchSizes = "watchSizes";

        this.subscription.add(
            this.variantsForm.controls[watchSizes].valueChanges.subscribe(
                (value) => {
                    const selectedVariant: AbstractProductVariant =
                        this.product.variants[watchSizes].find(
                            (variant: ProductSizeVariant) =>
                                variant.size === value,
                        );

                    this.navigateToVariant(
                        selectedVariant.slug,
                        selectedVariant.sku,
                    );
                },
            ),
        );
    }

    /**
     * Initialize strapColors form control
     */
    private initStrapColorFormControl(): void {
        const strapColors = "strapColors";

        this.subscription.add(
            this.variantsForm.controls[strapColors].valueChanges.subscribe(
                (value) => {
                    // get selected variant by comparing nbTours and refColoris
                    const selectedVariant: AbstractProductVariant =
                        this.product.variants[strapColors].find(
                            (variant: ProductWatchStrapVariant) =>
                                variant.refColoris ===
                                    JSON.parse(value).refColoris &&
                                variant.nbTours === JSON.parse(value).nbTours,
                        );
                    this.navigateToVariant(
                        selectedVariant.slug,
                        selectedVariant.sku,
                    );
                },
            ),
        );
    }
}
